import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostBinding, Inject, OnInit } from '@angular/core';
import { filter } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { MenuAsideOffcanvasDirective } from '../../../core/directives/menu-aside-offcanvas.directive';
import { ClassInitService } from '../../../core/services/class-init.service';
import { LayoutConfigService } from '../../../core/services/layout-config.service';
import { LayoutRefService } from '../../../core/services/layout/layout-ref.service';
import { MenuAsideService } from '../../../core/services/layout/menu-aside.service';
import { DOCUMENT } from '@angular/common';
import { urls } from '../../../global';
import { AppService } from '../../pages/components/_shared/app.service';
import { MenuConfigService } from '../../../core/services/menu-config.service';
import { TranslationService } from '../../../core/services/translation.service';
import { ChangeDetectorRef } from '@angular/core';

@Component({
	selector: 'm-aside-left',
	templateUrl: './aside-left.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AsideLeftComponent implements OnInit, AfterViewInit {

	@HostBinding('class') classes = 'm-grid__item m-aside-left';
	@HostBinding('id') id = 'm_aside_left';

	@HostBinding('attr.mMenuAsideOffcanvas') mMenuAsideOffcanvas: MenuAsideOffcanvasDirective;

	currentRouteUrl: string = '';
	insideTm: any;
	outsideTm: any;
	intervalId;

	constructor(
		private el: ElementRef,
		public classInitService: ClassInitService,
		public menuAsideService: MenuAsideService,
		public layoutConfigService: LayoutConfigService,
		private router: Router,
		private layoutRefService: LayoutRefService,
		private appService: AppService,
		private menuconfig: MenuConfigService,
		private translationService: TranslationService,
		private cdr: ChangeDetectorRef,
		@Inject(DOCUMENT) private document: Document
	) {
		// subscribe to menu classes update
		this.classInitService.onClassesUpdated$.subscribe(classes => {
			// join the classes array and pass to variable
			this.classes = 'm-grid__item m-aside-left ' + classes.aside_left.join(' ');
		});
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			this.mMenuAsideOffcanvas = new MenuAsideOffcanvasDirective(this.el);
			// manually call the directives' lifecycle hook method
			this.mMenuAsideOffcanvas.ngAfterViewInit();

			// keep aside left element reference
			this.layoutRefService.addElement('asideLeft', this.el.nativeElement);
		});
	}

	ngOnInit() {
		const LAST_VISIT_TIMESTAMP_KEY = 'lastVisitApiCallTime';
		const INTERVAL = 2 * 60 * 60 * 1000;

		this.callLastVisitCountIfNeeded(LAST_VISIT_TIMESTAMP_KEY, INTERVAL);

		this.intervalId = setInterval(() => {
			this.callLastVisitCountIfNeeded(LAST_VISIT_TIMESTAMP_KEY, INTERVAL);
		}, INTERVAL);

		this.currentRouteUrl = this.router.url.split(/[?#]/)[0];

		this.router.events
			.pipe(filter(event => event instanceof NavigationEnd))
			.subscribe(event => this.currentRouteUrl = this.router.url.split(/[?#]/)[0]);

		// Subscribe to language changes and trigger change detection
		this.translationService.langChange.subscribe(() => {
			this.cdr.markForCheck();
		});
	}

	callLastVisitCountIfNeeded(key: string, interval: number) {
		const lastCalled = localStorage.getItem(key);
		const now = Date.now();

		if (!lastCalled || now - parseInt(lastCalled, 10) >= interval) {
			this.appService.get(urls.lastVisitCount).subscribe((res: any) => {
				localStorage.setItem('stageCount', res.data.staging_count);
				localStorage.setItem('partsExpiringCount', res.data.parts_expiring);
				localStorage.setItem('partExpiredCount', res.data.parts_expired);
				localStorage.setItem(key, now.toString());
				this.menuconfig.resetModel();
			});
		}
	}

	// countData(){
	// 	this.appService.get(urls.lastVisitCount).subscribe((res: any)=>{
	// 		localStorage.setItem('stageCount', res.data.staging_count);
	// 		localStorage.setItem('partsExpiringCount', res.data.parts_expiring);
	// 		localStorage.setItem('partExpiredCount', res.data.parts_expired);
	// 		this.menuconfig.resetModel();
	// 	})
	// }

	isMenuItemIsActive(item): boolean {
		if (item.submenu) {
			return this.isMenuRootItemIsActive(item);
		}

		if (!item.page) {
			return false;
		}

		// dashboard
		if (item.page !== '/' && this.currentRouteUrl.startsWith(item.page)) {
			return true;
		}
		return this.currentRouteUrl === item.page;
	}

	isMenuRootItemIsActive(item): boolean {
		let result: boolean = false;

		for (const subItem of item.submenu) {
			result = this.isMenuItemIsActive(subItem);
			if (result) {
				return true;
			}
		}

		return false;
	}

	/**
	 * Use for fixed left aside menu, to show menu on mouseenter event.
	 * @param e Event
	 */
	mouseEnter(e: Event) {
		// check if the left aside menu is fixed
		if (this.document.body.classList.contains('m-aside-left--fixed')) {
			if (this.outsideTm) {
				clearTimeout(this.outsideTm);
				this.outsideTm = null;
			}

			this.insideTm = setTimeout(() => {
				// if the left aside menu is minimized
				if (this.document.body.classList.contains('m-aside-left--minimize') && mUtil.isInResponsiveRange('desktop')) {
					// show the left aside menu
					this.document.body.classList.remove('m-aside-left--minimize');
					this.document.body.classList.add('m-aside-left--minimize-hover');
				}
			}, 300);
		}
	}

	/**
	 * Use for fixed left aside menu, to show menu on mouseenter event.
	 * @param e Event
	 */
	mouseLeave(e: Event) {
		if (this.document.body.classList.contains('m-aside-left--fixed')) {
			if (this.insideTm) {
				clearTimeout(this.insideTm);
				this.insideTm = null;
			}

			this.outsideTm = setTimeout(() => {
				// if the left aside menu is expand
				if (this.document.body.classList.contains('m-aside-left--minimize-hover') && mUtil.isInResponsiveRange('desktop')) {
					// hide back the left aside menu
					this.document.body.classList.remove('m-aside-left--minimize-hover');
					this.document.body.classList.add('m-aside-left--minimize');
				}
			}, 500);
		}
	}

	ngOnDestroy() {
		if (this.intervalId) {
			clearInterval(this.intervalId); // 🧹 Prevent memory leaks
		}
	}
}
